import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchAvailableModels } from '../../lib/models';
import { fetchSettings, saveSettings } from '../../lib/api';
import { useAuth } from '../auth/useAuth';
import type { ChatOptions, ChatMode, RetrievalProfile, ServerSettings } from '../../types';
import { DEFAULT_CHAT_OPTIONS } from '../../types';

const OPTIONS_KEY = 'malpedia-chat-options';

const MODE_OPTIONS: { value: ChatMode; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'vector', label: 'Semantic' },
  { value: 'local', label: 'Connected' },
  { value: 'global', label: 'Entity' },
  { value: 'drift', label: 'Discovery' },
  { value: 'planned', label: 'Planned' },
  { value: 'llm_only', label: 'Chat only' },
];

const PROFILE_OPTIONS: { value: RetrievalProfile; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'narrow', label: 'Quick' },
  { value: 'normal', label: 'Balanced' },
  { value: 'broad', label: 'Thorough' },
  { value: 'exhaustive', label: 'Deep' },
  { value: 'manual', label: 'Custom' },
];

const BASE_BACKENDS = ['openai', 'anthropic', 'openai_compatible'] as const;

const FHGENIE_URLS = {
  default: 'https://fhgenie.fraunhofer.de/v1',
  preview: 'https://fhgenie-preview.c.fraunhofer.de/v1',
} as const;

const BASE_URL_PLACEHOLDERS: Record<string, string> = {
  openai: 'https://api.openai.com/v1',
  anthropic: 'https://api.anthropic.com',
  openai_compatible: 'http://localhost:8080/v1',
  fhgenie: FHGENIE_URLS.default,
};

const BACKEND_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  openai_compatible: 'OpenAI-compatible',
  fhgenie: 'FHGenie',
};

function loadLocalOptions(): ChatOptions {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    if (!raw) return { ...DEFAULT_CHAT_OPTIONS };
    return { ...DEFAULT_CHAT_OPTIONS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_CHAT_OPTIONS };
  }
}

function persistLocalOptions(opts: ChatOptions) {
  try {
    localStorage.setItem(OPTIONS_KEY, JSON.stringify(opts));
  } catch {
    /* quota exceeded */
  }
}

/** Combo input: text field + "Load models" button with filterable suggestions. */
function ModelSelect({
  backend,
  baseUrl,
  value,
  onChange,
  apiKey,
  hasServerKey,
}: {
  backend: string;
  baseUrl: string;
  value: string;
  onChange: (model: string) => void;
  apiKey?: string;
  hasServerKey?: boolean;
}) {
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const prevBackend = useRef(backend);
  const prevBaseUrl = useRef(baseUrl);

  // Clear suggestions when backend or baseUrl changes
  useEffect(() => {
    if (prevBackend.current !== backend || prevBaseUrl.current !== baseUrl) {
      setModels([]);
      setFetchError(false);
      setShowSuggestions(false);
      prevBackend.current = backend;
      prevBaseUrl.current = baseUrl;
    }
  }, [backend, baseUrl]);

  // Click outside to close suggestions
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const result = await fetchAvailableModels(backend, baseUrl, apiKey || undefined);
      setModels(result);
      setShowSuggestions(result.length > 0);
      if (result.length === 0) setFetchError(true);
    } catch {
      setModels([]);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [backend, baseUrl, apiKey]);

  const filtered = models.filter((m) =>
    m.toLowerCase().includes(value.toLowerCase()),
  );

  const canFetch = !!(apiKey || hasServerKey);

  return (
    <div className="malpedia-settings-model-combo" ref={wrapRef}>
      <input
        type="text"
        className="malpedia-settings-input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (models.length > 0) setShowSuggestions(true);
        }}
        onFocus={() => {
          if (models.length > 0) setShowSuggestions(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setShowSuggestions(false);
        }}
        placeholder="e.g. gpt-4o"
      />
      <button
        type="button"
        className="malpedia-settings-fetch-btn"
        onClick={handleFetch}
        disabled={loading}
        title={canFetch ? 'Load available models' : 'Save an API key first, then load models'}
      >
        {loading ? (
          <span className="malpedia-settings-spinner" />
        ) : (
          '↻'
        )}
      </button>

      {fetchError && models.length === 0 && !loading && (
        <div className="malpedia-settings-model-empty">
          No models found{!canFetch ? ' — save an API key first' : ''}
        </div>
      )}

      {showSuggestions && filtered.length > 0 && (
        <div className="malpedia-settings-model-suggestions">
          {filtered.map((m) => (
            <button
              key={m}
              type="button"
              className="malpedia-settings-model-suggestion"
              onClick={() => {
                onChange(m);
                setShowSuggestions(false);
              }}
            >
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface FormState {
  options: ChatOptions;
  llmApiKey: string;
  plannerLlmApiKey: string;
}

export default function SettingsPage() {
  const { data: user } = useAuth();
  const isFKIE = user?.groups?.includes('FKIE') ?? false;

  // Build backends list dynamically based on user role
  const backends = isFKIE
    ? (['fhgenie', 'openai', 'anthropic', 'openai_compatible'] as const)
    : BASE_BACKENDS;

  const { data: serverSettings, isLoading: serverLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings,
    staleTime: 60_000,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasServerLlmKey, setHasServerLlmKey] = useState(false);
  const [llmKeyHint, setLlmKeyHint] = useState('');
  const [hasServerPlannerKey, setHasServerPlannerKey] = useState(false);
  const [plannerKeyHint, setPlannerKeyHint] = useState('');
  const [showMigrationBanner, setShowMigrationBanner] = useState(false);
  const [useSeparatePlanner, setUseSeparatePlanner] = useState(false);

  // Initialize form from server or localStorage
  useEffect(() => {
    if (serverLoading) return;

    if (serverSettings) {
      // Server has settings — use them
      const opts: ChatOptions = {
        mode: (serverSettings.mode as ChatMode) || DEFAULT_CHAT_OPTIONS.mode,
        llmBackend: (serverSettings.llmBackend as ChatOptions['llmBackend']) || 'openai',
        llmModel: serverSettings.llmModel || '',
        llmBaseUrl: serverSettings.llmBaseUrl || '',
        plannerLlmBackend:
          (serverSettings.plannerLlmBackend as ChatOptions['plannerLlmBackend']) || 'openai',
        plannerLlmModel: serverSettings.plannerLlmModel || '',
        plannerLlmBaseUrl: serverSettings.plannerLlmBaseUrl || '',
        retrievalProfile:
          (serverSettings.retrievalProfile as RetrievalProfile) || 'auto',
        topK: serverSettings.topK ?? 10,
        maxContextTokens: serverSettings.maxContextTokens ?? 0,
        iocPivot: serverSettings.iocPivot ?? true,
        webSearch: (serverSettings as any).webSearch ?? false,
        neighborExpansion: (serverSettings as any).neighborExpansion ?? false,
        fullDocContext: (serverSettings as any).fullDocContext ?? false,
        useV2IocGraph: (serverSettings as any).useV2IocGraph ?? false,
        customTopSources: (serverSettings as any).customTopSources ?? 8,
        customFusionCap: (serverSettings as any).customFusionCap ?? 20,
        customMaxChunksPerDoc: (serverSettings as any).customMaxChunksPerDoc ?? 0,
      };
      if (opts.llmBackend === 'fhgenie' && !opts.llmBaseUrl) {
        opts.llmBaseUrl = FHGENIE_URLS.default;
      }
      if (opts.plannerLlmBackend === 'fhgenie' && !opts.plannerLlmBaseUrl) {
        opts.plannerLlmBaseUrl = FHGENIE_URLS.default;
      }
      setForm({ options: opts, llmApiKey: '', plannerLlmApiKey: '' });
      setHasServerLlmKey(serverSettings.hasLlmApiKey);
      setLlmKeyHint(serverSettings.llmApiKeyHint || '');
      setHasServerPlannerKey(serverSettings.hasPlannerLlmApiKey);
      setPlannerKeyHint(serverSettings.plannerLlmApiKeyHint || '');

      // Derive planner toggle state: ON if planner differs from main
      const hasSeparate =
        (serverSettings.plannerLlmModel && serverSettings.plannerLlmModel !== serverSettings.llmModel) ||
        (serverSettings.plannerLlmBackend && serverSettings.plannerLlmBackend !== serverSettings.llmBackend);
      setUseSeparatePlanner(!!hasSeparate);

      // Also sync non-secret settings to localStorage for ChatApp
      persistLocalOptions(opts);
    } else {
      // No server settings — fall back to localStorage (with FKIE defaults)
      const local = loadLocalOptions();
      if (isFKIE) {
        local.llmBackend = 'fhgenie';
        local.llmModel = local.llmModel || 'MiniMaxAI/MiniMax-M2.5';
        local.llmBaseUrl = FHGENIE_URLS.default;
      }
      setForm({ options: local, llmApiKey: '', plannerLlmApiKey: '' });
      // Check if localStorage has API keys worth migrating
      try {
        const raw = localStorage.getItem(OPTIONS_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.llmApiKey || parsed.plannerLlmApiKey) {
            setShowMigrationBanner(true);
          }
        }
      } catch {
        /* ignore */
      }
    }
  }, [serverSettings, serverLoading, isFKIE]);

  const updateOptions = useCallback((partial: Partial<ChatOptions>) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, options: { ...prev.options, ...partial } };
      return next;
    });
    setIsDirty(true);
  }, []);

  const updateApiKey = useCallback((field: 'llmApiKey' | 'plannerLlmApiKey', value: string) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!form) return;
    setSaving(true);
    try {
      // If separate planner is off, clear planner fields so backend falls back
      const plannerBackend = useSeparatePlanner ? form.options.plannerLlmBackend : '';
      const plannerModel = useSeparatePlanner ? form.options.plannerLlmModel : '';
      const plannerBaseUrl = useSeparatePlanner ? form.options.plannerLlmBaseUrl : '';

      const isTest = serverSettings?.isTestAccount ?? false;
      const body: Record<string, unknown> = {
        mode: form.options.mode,
        retrievalProfile: form.options.retrievalProfile,
        topK: form.options.topK,
        maxContextTokens: form.options.maxContextTokens,
        iocPivot: form.options.iocPivot,
        webSearch: form.options.webSearch,
      };
      // Test accounts: only send retrieval fields (LLM fields are locked)
      if (!isTest) {
        body.llmBackend = form.options.llmBackend;
        body.llmModel = form.options.llmModel;
        body.llmBaseUrl = form.options.llmBaseUrl;
        body.plannerLlmBackend = plannerBackend;
        body.plannerLlmModel = plannerModel;
        body.plannerLlmBaseUrl = plannerBaseUrl;
        // Only send API key if user typed something
        if (form.llmApiKey) {
          body.llmApiKey = form.llmApiKey;
        }
        if (useSeparatePlanner && form.plannerLlmApiKey) {
          body.plannerLlmApiKey = form.plannerLlmApiKey;
        }
        if (!useSeparatePlanner) {
          // Clear planner key on server
          body.plannerLlmApiKey = '';
        }
      }

      const result = await saveSettings(body);
      setHasServerLlmKey(result.hasLlmApiKey);
      setLlmKeyHint(result.llmApiKeyHint || '');
      setHasServerPlannerKey(result.hasPlannerLlmApiKey);
      setPlannerKeyHint(result.plannerLlmApiKeyHint || '');
      setForm((prev) => (prev ? { ...prev, llmApiKey: '', plannerLlmApiKey: '' } : prev));
      setIsDirty(false);
      setShowMigrationBanner(false);
      // Sync non-secret settings to localStorage for ChatApp
      persistLocalOptions(form.options);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [form, useSeparatePlanner]);

  const handleClearKey = useCallback(
    async (field: 'llmApiKey' | 'plannerLlmApiKey') => {
      if (!form) return;
      setSaving(true);
      try {
        const body: Record<string, unknown> = { [field]: '' };
        const result = await saveSettings(body);
        setHasServerLlmKey(result.hasLlmApiKey);
        setLlmKeyHint(result.llmApiKeyHint || '');
        setHasServerPlannerKey(result.hasPlannerLlmApiKey);
        setPlannerKeyHint(result.plannerLlmApiKeyHint || '');
        toast.success('API key cleared');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to clear key');
      } finally {
        setSaving(false);
      }
    },
    [form],
  );

  if (serverLoading || !form) {
    return (
      <div className="malpedia-settings-page">
        <div className="malpedia-settings-container">
          <h1 className="malpedia-settings-title">Settings</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const { options } = form;
  const isTestAccount = serverSettings?.isTestAccount ?? false;
  const isFhgenie = options.llmBackend === 'fhgenie';
  const isPlannerFhgenie = options.plannerLlmBackend === 'fhgenie';
  const fhgenieUrl = options.llmBaseUrl === FHGENIE_URLS.preview ? 'preview' : 'default';
  const plannerFhgenieUrl = options.plannerLlmBaseUrl === FHGENIE_URLS.preview ? 'preview' : 'default';

  return (
    <div className="malpedia-settings-page">
      <div className="malpedia-settings-container">
        <h1 className="malpedia-settings-title">Settings</h1>

        {showMigrationBanner && (
          <div className="malpedia-settings-migration-banner">
            <p>
              You have settings with API keys stored in your browser. Click <strong>Save</strong> to
              migrate them securely to the server.
            </p>
          </div>
        )}

        {isTestAccount && (
          <div className="malpedia-settings-test-banner">
            This is a test account. LLM settings are managed by an administrator.
          </div>
        )}

        {/* ── Retrieval Settings ────────────────────────────────────── */}
        <section className="malpedia-settings-section">
          <h2 className="malpedia-settings-section-title">Retrieval</h2>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Search strategy</label>
            <select
              className="malpedia-settings-select"
              value={options.mode}
              onChange={(e) => updateOptions({ mode: e.target.value as ChatMode })}
            >
              {MODE_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Search depth</label>
            <select
              className="malpedia-settings-select"
              value={options.retrievalProfile}
              onChange={(e) => updateOptions({ retrievalProfile: e.target.value as RetrievalProfile })}
            >
              {PROFILE_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">
              Candidates
              <span className="malpedia-settings-value">{options.topK}</span>
            </label>
            <input
              type="range"
              className="malpedia-settings-slider"
              min={1}
              max={1000}
              value={options.topK}
              onChange={(e) => updateOptions({ topK: Number(e.target.value) })}
            />
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">IOC search</label>
            <label className="malpedia-settings-toggle">
              <input
                type="checkbox"
                checked={options.iocPivot}
                onChange={(e) => updateOptions({ iocPivot: e.target.checked })}
              />
              <span className="malpedia-settings-toggle-track" />
            </label>
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Web search</label>
            <label className="malpedia-settings-toggle">
              <input
                type="checkbox"
                checked={options.webSearch}
                onChange={(e) => updateOptions({ webSearch: e.target.checked })}
              />
              <span className="malpedia-settings-toggle-track" />
            </label>
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Expand context</label>
            <label className="malpedia-settings-toggle">
              <input
                type="checkbox"
                checked={options.neighborExpansion}
                onChange={(e) => updateOptions({ neighborExpansion: e.target.checked })}
              />
              <span className="malpedia-settings-toggle-track" />
            </label>
          </div>
        </section>

        {/* ── LLM Settings ─────────────────────────────────────────── */}
        <section className="malpedia-settings-section">
          <h2 className="malpedia-settings-section-title">LLM</h2>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Backend</label>
            <select
              className="malpedia-settings-select"
              value={options.llmBackend}
              onChange={(e) => updateOptions({ llmBackend: e.target.value as ChatOptions['llmBackend'] })}
              disabled={isTestAccount}
            >
              {backends.map((b) => (
                <option key={b} value={b}>{BACKEND_LABELS[b] ?? b}</option>
              ))}
            </select>
          </div>

          {isFhgenie ? (
            <div className="malpedia-settings-row">
              <label className="malpedia-settings-label">Base URL</label>
              <div className="malpedia-settings-url-pills">
                <button
                  type="button"
                  className={`malpedia-settings-url-pill${fhgenieUrl === 'default' ? ' active' : ''}`}
                  onClick={() => updateOptions({ llmBaseUrl: FHGENIE_URLS.default })}
                  title={FHGENIE_URLS.default}
                  disabled={isTestAccount}
                >Default</button>
                <button
                  type="button"
                  className={`malpedia-settings-url-pill${fhgenieUrl === 'preview' ? ' active' : ''}`}
                  onClick={() => updateOptions({ llmBaseUrl: FHGENIE_URLS.preview })}
                  title={FHGENIE_URLS.preview}
                  disabled={isTestAccount}
                >Preview</button>
              </div>
            </div>
          ) : (
            <div className="malpedia-settings-row">
              <label className="malpedia-settings-label">Base URL</label>
              <input
                type="text"
                className="malpedia-settings-input"
                value={options.llmBaseUrl}
                onChange={(e) => updateOptions({ llmBaseUrl: e.target.value })}
                placeholder={BASE_URL_PLACEHOLDERS[options.llmBackend] ?? ''}
                disabled={isTestAccount}
              />
            </div>
          )}

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Model</label>
            {isTestAccount ? (
              <input
                type="text"
                className="malpedia-settings-input"
                value={options.llmModel}
                disabled
              />
            ) : (
              <ModelSelect
                backend={options.llmBackend}
                baseUrl={options.llmBaseUrl}
                value={options.llmModel}
                onChange={(model) => updateOptions({ llmModel: model })}
                apiKey={form.llmApiKey}
                hasServerKey={hasServerLlmKey}
              />
            )}
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">API Key</label>
            <div className="malpedia-settings-password-wrap">
              <input
                type="password"
                className="malpedia-settings-input"
                value={isTestAccount ? '' : form.llmApiKey}
                onChange={(e) => updateApiKey('llmApiKey', e.target.value)}
                placeholder={isTestAccount ? 'admin-managed' : hasServerLlmKey ? `${llmKeyHint} (saved)` : 'sk-...'}
                autoComplete="off"
                disabled={isTestAccount}
              />
              {hasServerLlmKey && !isTestAccount && (
                <button
                  type="button"
                  className="malpedia-settings-eye-btn"
                  onClick={() => handleClearKey('llmApiKey')}
                  title="Clear stored API key"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="malpedia-settings-row">
            <label className="malpedia-settings-label">Max Context Tokens</label>
            <input
              type="number"
              className="malpedia-settings-input malpedia-settings-input--narrow"
              value={options.maxContextTokens}
              onChange={(e) => updateOptions({ maxContextTokens: Number(e.target.value) || 0 })}
              min={0}
              placeholder="0 = unlimited"
            />
          </div>
        </section>

        {/* ── Planner Settings (hidden for test accounts) ──────────── */}
        {!isTestAccount && <section className="malpedia-settings-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h2 className="malpedia-settings-section-title" style={{ margin: 0 }}>Planner LLM</h2>
            <label className="malpedia-settings-toggle">
              <input
                type="checkbox"
                checked={useSeparatePlanner}
                onChange={(e) => {
                  const on = e.target.checked;
                  setUseSeparatePlanner(on);
                  setIsDirty(true);
                  if (on) {
                    // Pre-fill with current main LLM values
                    updateOptions({
                      plannerLlmBackend: options.llmBackend,
                      plannerLlmModel: options.llmModel,
                      plannerLlmBaseUrl: options.llmBaseUrl,
                    });
                    if (form.llmApiKey) {
                      updateApiKey('plannerLlmApiKey', form.llmApiKey);
                    }
                  }
                }}
              />
              <span className="malpedia-settings-toggle-track" />
            </label>
            <span className="malpedia-settings-hint" style={{ margin: 0 }}>
              Different LLM for planning
            </span>
          </div>

          {useSeparatePlanner && (
            <>
              <p className="malpedia-settings-hint">
                A separate LLM used to analyse your query and plan which retrieval
                strategies to use. Defaults to the main LLM above if left blank.
              </p>

              <div className="malpedia-settings-row">
                <label className="malpedia-settings-label">Backend</label>
                <select
                  className="malpedia-settings-select"
                  value={options.plannerLlmBackend}
                  onChange={(e) =>
                    updateOptions({ plannerLlmBackend: e.target.value as ChatOptions['plannerLlmBackend'] })
                  }
                >
                  {backends.map((b) => (
                    <option key={b} value={b}>{BACKEND_LABELS[b] ?? b}</option>
                  ))}
                </select>
              </div>

              {isPlannerFhgenie ? (
                <div className="malpedia-settings-row">
                  <label className="malpedia-settings-label">Base URL</label>
                  <div className="malpedia-settings-url-pills">
                    <button
                      type="button"
                      className={`malpedia-settings-url-pill${plannerFhgenieUrl === 'default' ? ' active' : ''}`}
                      onClick={() => updateOptions({ plannerLlmBaseUrl: FHGENIE_URLS.default })}
                      title={FHGENIE_URLS.default}
                    >Default</button>
                    <button
                      type="button"
                      className={`malpedia-settings-url-pill${plannerFhgenieUrl === 'preview' ? ' active' : ''}`}
                      onClick={() => updateOptions({ plannerLlmBaseUrl: FHGENIE_URLS.preview })}
                      title={FHGENIE_URLS.preview}
                    >Preview</button>
                  </div>
                </div>
              ) : (
                <div className="malpedia-settings-row">
                  <label className="malpedia-settings-label">Base URL</label>
                  <input
                    type="text"
                    className="malpedia-settings-input"
                    value={options.plannerLlmBaseUrl}
                    onChange={(e) => updateOptions({ plannerLlmBaseUrl: e.target.value })}
                    placeholder={BASE_URL_PLACEHOLDERS[options.plannerLlmBackend] ?? ''}
                  />
                </div>
              )}

              <div className="malpedia-settings-row">
                <label className="malpedia-settings-label">Model</label>
                <ModelSelect
                  backend={options.plannerLlmBackend}
                  baseUrl={options.plannerLlmBaseUrl}
                  value={options.plannerLlmModel}
                  onChange={(model) => updateOptions({ plannerLlmModel: model })}
                  apiKey={form.plannerLlmApiKey}
                  hasServerKey={hasServerPlannerKey}
                />
              </div>

              <div className="malpedia-settings-row">
                <label className="malpedia-settings-label">API Key</label>
                <div className="malpedia-settings-password-wrap">
                  <input
                    type="password"
                    className="malpedia-settings-input"
                    value={form.plannerLlmApiKey}
                    onChange={(e) => updateApiKey('plannerLlmApiKey', e.target.value)}
                    placeholder={hasServerPlannerKey ? `${plannerKeyHint} (saved)` : 'sk-...'}
                    autoComplete="off"
                  />
                  {hasServerPlannerKey && (
                    <button
                      type="button"
                      className="malpedia-settings-eye-btn"
                      onClick={() => handleClearKey('plannerLlmApiKey')}
                      title="Clear stored API key"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </section>}

        {/* ── Save Button ──────────────────────────────────────────── */}
        <div className="malpedia-settings-actions">
          <button
            type="button"
            className="malpedia-settings-save-btn"
            disabled={!isDirty || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
